import React, { useState, useEffect, useRef, useMemo } from 'react'
import { Plus } from 'lucide-react'
import { FormikProps } from 'formik'
import toast from 'react-hot-toast'
import { useContactOwners } from 'service/crm'
import { useAppSelector } from 'store'
import { selectAuthUser } from 'store/slices/auth/auth.selector'
import { CrmTask, TaskFormValues } from 'types/crm/task'
import CreateTask from './CreateTask'
import TaskList from './TaskList'
import { CrmCommentHooks } from 'types/crm/comments'
import { formatDateToYmd } from 'utils/date'
import { useRecordAccess } from 'context/crm/RecordAccessContext'
import DeleteModal, { CascadeItem } from 'components/Modals/CrmDeleteModal'


export interface TaskQueryResult {
    tasks: any[]
    isLoading: boolean
    hasMore: boolean
    isFetchingMore: boolean
    onLoadMore: () => void
    refetch: () => void
}

interface TasksTabProps {
    isActive: boolean
    openTrigger?: number
    entityUuid: string
    useTasksQuery: (entityUuid: string) => TaskQueryResult
    useCreateTask: () => { mutateAsync: (payload: any) => Promise<any>; isPending: boolean }
    buildCreatePayload: (entityUuid: string, values: TaskFormValues) => any
    /** Mutation hook for editing a task. Same shape as useCreateTask. */
    useUpdateTask: () => { mutateAsync: (payload: any) => Promise<any>; isPending: boolean }
    /** Builds the update payload from the entity uuid, the task being edited, and the new form values. */
    buildUpdatePayload: (entityUuid: string, taskUuid: string, values: TaskFormValues) => any
    /** Mutation hook for deleting a task. */
    useDeleteTask: () => { mutateAsync: (payload: any) => Promise<any>; isPending: boolean }
    /** Builds the delete payload from the entity uuid and task uuid. Lets each entity
     *  (contact/deal/company) decide its own key name — contactUuid, dealUuid, companyUuid —
     *  instead of this shared component hardcoding one. */
    buildDeletePayload: (entityUuid: string, taskUuid: string) => any
    commentHooks: CrmCommentHooks
    /** Task uuid to scroll to and highlight, e.g. from a ?taskUuid= deep link. */
    highlightTaskUuid?: string | null
    /** Called once the highlighted row has been scrolled into view. */
    onTaskHighlighted?: () => void
}

const TasksTab = ({
    isActive,
    openTrigger,
    entityUuid,
    useTasksQuery,
    useCreateTask,
    buildCreatePayload,
    useUpdateTask,
    buildUpdatePayload,
    useDeleteTask,
    buildDeletePayload,
    commentHooks,
    highlightTaskUuid,
    onTaskHighlighted
}: TasksTabProps) => {
    const [isOpen, setIsOpen] = useState(false)
    const [editingTask, setEditingTask] = useState<CrmTask | null>(null)
    // Task pending delete confirmation via WarningDialog. Null = dialog closed.
    const [deleteTarget, setDeleteTarget] = useState<CrmTask | null>(null)
    const formikRef = useRef<FormikProps<TaskFormValues>>(null)

    /**
     * RBAC: `canCreateTask` gates creating, `canEdit`/`canDelete` gate mutating an
     * existing task. TaskList stays permission-unaware — omitting a callback
     * hides that affordance, and omitting onUpdateTask also disables the inline
     * type/priority/status pills via its own `disabled={!onUpdateTask}`.
     * Viewing the list and commenting are never gated (sections 4, 14, 15).
     */
    const { canCreateTask, canEdit, canDelete } = useRecordAccess()
    const canUseForm = canCreateTask || canEdit

    const authUser = useAppSelector(selectAuthUser)

    const { data: owners = [] } = useContactOwners(authUser?.orgUuid as string)
    const { tasks, isLoading, hasMore, isFetchingMore, onLoadMore, refetch } = useTasksQuery(entityUuid)
    const createTaskMutation = useCreateTask()
    const updateTaskMutation = useUpdateTask()
    const deleteTaskMutation = useDeleteTask()

    const closeForm = () => {
        setIsOpen(false)
        setEditingTask(null)
    }

    const openCreateForm = () => {
        if (!canCreateTask) return
        setEditingTask(null)
        setIsOpen(true)
    }

    useEffect(() => {
        if (!openTrigger) return
        if (!canCreateTask) return
        if (isOpen) {
            formikRef.current?.resetForm()
        } else {
            setEditingTask(null)
            setIsOpen(true)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [openTrigger])

    useEffect(() => {
        if (!isActive && isOpen) {
            formikRef.current?.resetForm()
            closeForm()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isActive])

    // Section 23: access can disappear mid-session (Task/Note reassigned, Owner
    // changed). Collapse any open form rather than leaving a dead one on screen.
    useEffect(() => {
        if (!canUseForm) {
            closeForm()
            setDeleteTarget(null)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canUseForm])

    const handleSubmit = async (
        values: TaskFormValues,
        { resetForm }: { resetForm: () => void },
    ) => {
        if (!authUser?.appUserUuid || !entityUuid) {
            toast.error('Missing authentication context')
            return
        }
        if (editingTask ? !canEdit : !canCreateTask) {
            toast.error('You do not have permission to modify tasks on this record.')
            closeForm()
            return
        }

        const title = values.title?.trim()
        if (!title) {
            toast.error('Task title cannot be empty')
            return
        }

        const dueAt = values.dueAt || formatDateToYmd(new Date()) // default to today, YYYY-MM-DD
        const formValues = { ...values, dueAt }

        try {
            if (editingTask) {
                await updateTaskMutation.mutateAsync(
                    buildUpdatePayload(entityUuid, editingTask.uuid, formValues)
                )
                toast.success('Task updated')
            } else {
                await createTaskMutation.mutateAsync(
                    buildCreatePayload(entityUuid, formValues)
                )
                toast.success('Task created')
            }
            resetForm()
            closeForm()
        } catch (err: any) {
            toast.error(
                err?.message ??
                (editingTask
                    ? 'Failed to update task. Please try again.'
                    : 'Failed to create task. Please try again.')
            )
        }
    }

    const handleUpdateTask = async (
        taskUuid: string,
        updates: Partial<Pick<CrmTask, 'taskType' | 'priority' | 'taskStatus'>>,
    ) => {
        if (!entityUuid) return
        // Inline pill changes in TaskList are edits too — gate them.
        if (!canEdit) {
            toast.error('You do not have permission to modify tasks on this record.')
            return
        }

        const task = tasks?.find((t) => t.uuid === taskUuid)
        if (!task) return

        const formValues: TaskFormValues = {
            title: task.title ?? '',
            description: task.description ?? '',
            taskType: task.taskType,
            priority: task.priority,
            taskStatus: task.taskStatus,
            taskOwnerUuid: task.owner?.uuid ?? null,
            dueAt: task.dueAt ? formatDateToYmd(new Date(task.dueAt)) : '',
            ...updates,
        }

        try {
            await updateTaskMutation.mutateAsync(
                buildUpdatePayload(entityUuid, taskUuid, formValues)
            )
            refetch()
        } catch (err: any) {
            toast.error(err?.message ?? 'Failed to update task. Please try again.')
        }
    }

    const formContainerRef = useRef<HTMLDivElement | null>(null)

    const openEditForm = (task: CrmTask) => {
        if (!canEdit) return
        setEditingTask(task)
        setIsOpen(true)
    }

    useEffect(() => {
        if (isOpen && editingTask) {
            formContainerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
    }, [isOpen, editingTask])

    // Opens the WarningDialog rather than deleting immediately.
    const requestDeleteTask = (taskUuid: string) => {
        if (!canDelete) return
        const task = tasks?.find((t) => t.uuid === taskUuid) ?? null
        setDeleteTarget(task ?? ({ uuid: taskUuid } as CrmTask))
    }

    const handleCancelDelete = () => setDeleteTarget(null)

    const handleConfirmDelete = async () => {
        if (!deleteTarget || !entityUuid) return
        if (!canDelete) {
            toast.error('You do not have permission to delete tasks on this record.')
            setDeleteTarget(null)
            return
        }

        try {
            await deleteTaskMutation.mutateAsync(
                buildDeletePayload(entityUuid, deleteTarget.uuid),
            )
            toast.success('Task deleted')
            // If the task being edited was just deleted, close the form too.
            if (editingTask?.uuid === deleteTarget.uuid) closeForm()
        } catch (err: any) {
            toast.error(err?.message ?? 'Failed to delete task. Please try again.')
        } finally {
            setDeleteTarget(null)
        }
    }
    /** The task plus its comment thread. Comment row is omitted when there are none. */
    const deleteCascade = useMemo<CascadeItem[]>(() => {
        if (!deleteTarget) return []

        const { commentCount, comments } = deleteTarget as CrmTask &
            Partial<{ commentsCount: number; comments: unknown[] }>
        const commentTotal = commentCount ?? comments?.length

        return [
            // Falls back to a bare "Task" when requestDeleteTask synthesised the
            // target and we have no title to show.
            { count: 1, label: deleteTarget.title ? `Task — ${deleteTarget.title}` : 'Task' },
            ...(commentTotal ? [{ count: commentTotal, label: 'Comment' }] : []),
        ]
    }, [deleteTarget])

    if (!isActive) return null

    return (
        <div className="space-y-4">
            {canCreateTask && !isOpen && tasks?.length > 0 && (
                <button
                    onClick={openCreateForm}
                    className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg border border-dashed border-primary-subtle text-neutral-faded text-sm font-medium hover:bg-primary-surface hover:text-neutral transition-colors"
                >
                    <Plus className="w-4 h-4" />
                    Add a task
                </button>
            )}

            {isOpen && entityUuid && canUseForm && (
                <div ref={formContainerRef}>
                    <CreateTask
                        entityUuid={entityUuid}
                        owners={owners}
                        task={editingTask}
                        formikRef={formikRef}
                        onCancel={closeForm}
                        onSubmit={handleSubmit}
                        isSubmitting={createTaskMutation.isPending || updateTaskMutation.isPending}
                    />
                </div>
            )}
            {/* onToggleComplete intentionally left unwired: no update/complete-task mutation
                exists yet. Add one and pass it through here once available —
                gated on canEdit, since completing a task is a modification. */}
            <TaskList
                tasks={tasks}
                isLoading={isLoading}
                hasMore={hasMore}
                isFetchingMore={isFetchingMore}
                onLoadMore={onLoadMore}
                isFormOpen={isOpen}
                owners={owners}
                parentUuid={entityUuid}
                commentHooks={commentHooks}
                onCommentAdded={refetch}
                highlightTaskUuid={highlightTaskUuid}
                onTaskHighlighted={onTaskHighlighted}
                // Omitting these hides the empty-state CTA, the per-task action
                // menu, and disables the inline pills — TaskList already treats
                // undefined as "hide / disable".
                onAddTask={canCreateTask ? openCreateForm : undefined}
                onEditTask={canEdit ? openEditForm : undefined}
                onDeleteTask={canDelete ? requestDeleteTask : undefined}
                onUpdateTask={canEdit ? handleUpdateTask : undefined}
            />

            <DeleteModal
                isOpen={!!deleteTarget}
                onClose={handleCancelDelete}
                onConfirm={handleConfirmDelete}
                isDeleting={deleteTaskMutation.isPending}
                entityLabel="Task"
                cascade={deleteCascade}
            />
        </div>
    )
}

export default TasksTab